import type { VlanZone } from '@/api/vendo';
import { canHostVlans } from '@/utils/vlanHosts';

/**
 * The VLAN rules of the Add Sub-vendo wizard (Ray, 2026-10-15: "it created even
 * it doesnt have vlan yet"). On a main that can host VLANs, the Register step's
 * "VLAN zone" card picks a free VLAN, or creates one when none is found, and
 * Next stays locked until the router has it — and while the main is offline.
 */

/** Must a sub on this main sit on a VLAN? Every board that can host VLAN
 *  sub-vendos, except a grandfathered `vlanEnabled` main (it still takes the
 *  legacy per-sub VLAN fields). */
export function vlanRequiredFor(router?: {
   platform?: string;
   hwBoard?: string;
   vlanEnabled?: boolean;
}): boolean {
   if (router?.vlanEnabled === true) return false;
   return canHostVlans(router?.platform, router?.hwBoard);
}

/** First suggested VLAN id: 22, then 23, 24 … (Ray, 2026-10-15), skipping
 *  every id already a zone and the ids the server reserves (the main's own
 *  isolation VLAN, the internet VLAN tag). */
export function suggestVlanId(taken: number[]): number {
   const set = new Set(taken);
   for (let n = 22; n <= 4094; n++) if (!set.has(n)) return n;
   for (let n = 10; n < 22; n++) if (!set.has(n)) return n;
   return 22;
}

export type ChosenZoneState = 'none' | 'pending' | 'failed' | 'live';

/** State of the chosen VLAN. Next is allowed only on 'live' — a pending zone
 *  is not on the router yet, attaching to it would orphan the sub. A VLAN
 *  already used by another sub counts as 'none'. */
export function chosenZoneState(zones: VlanZone[], vlanRef: number | ''): ChosenZoneState {
   if (vlanRef === '') return 'none';
   const z = zones.find((x) => x.vlanId === vlanRef && !x.attachedSub);
   if (!z) return 'none';
   return z.status;
}

/** Poll the zone list while a free zone is still turning on. */
export function shouldPollZones(zones: VlanZone[]): boolean {
   return zones.some((z) => !z.attachedSub && z.status === 'pending');
}

/** Is the MAIN router reachable right now? Creating a VLAN needs the router to
 *  take it, so the wizard locks while it is offline. */
export function mainRouterOnline(raw?: { routerStatus?: string; status?: string }): boolean {
   const rs = raw?.routerStatus ?? (raw?.status === 'online' ? 'connected' : 'disconnected');
   return rs === 'connected';
}
